/**
 * API client functions for document versioning
 *
 * Provides functions to fetch version history and version details
 * for documents in the knowledge base.
 */
import { useAuth } from "@clerk/clerk-react";
import { createApiClient } from "./client";
import type {
  DocumentVersionSummary,
  DocumentVersionDetail,
  DocumentVersionListResponse,
  GetVersionsParams,
} from "../../types/version";

/**
 * Hook providing document version API functions
 *
 * @example
 * ```tsx
 * const { getVersions, getVersion } = useVersionsApi();
 *
 * // Fetch all versions for document 42
 * const versions = await getVersions({ documentId: 42 });
 *
 * // Fetch specific version
 * const version = await getVersion(42, 3);
 * ```
 */
export function useVersionsApi() {
  const { getToken } = useAuth();
  const api = createApiClient(() => getToken());

  return {
    /**
     * Get paginated list of versions for a document
     *
     * @param params - Query parameters for version list
     * @returns Paginated list of version summaries
     */
    async getVersions(
      params: GetVersionsParams
    ): Promise<DocumentVersionListResponse> {
      const { documentId, page = 1, pageSize = 20 } = params;

      const response = await api.get<DocumentVersionListResponse>(
        `/documents/${documentId}/versions`,
        {
          params: {
            page,
            page_size: pageSize,
          },
        }
      );

      return response.data;
    },

    /**
     * Get full details of a specific document version
     *
     * @param documentId - The document ID
     * @param versionNumber - The version number to fetch
     * @returns Full version details including content
     */
    async getVersion(
      documentId: number,
      versionNumber: number
    ): Promise<DocumentVersionDetail> {
      const response = await api.get<DocumentVersionDetail>(
        `/documents/${documentId}/versions/${versionNumber}`
      );

      return response.data;
    },

    /**
     * Get all versions for a document (unpaginated, for sidebar)
     * Fetches with large page size to get all versions in one request
     *
     * @param documentId - The document ID
     * @returns Array of all version summaries
     */
    async getAllVersions(documentId: number): Promise<DocumentVersionSummary[]> {
      const response = await api.get<DocumentVersionListResponse>(
        `/documents/${documentId}/versions`,
        {
          params: {
            page: 1,
            page_size: 100, // Max allowed by API
          },
        }
      );

      return response.data.versions;
    },
  };
}
